<!--
  Контейнер карточки ЭДО-документа.
  Загружает документ, управляет представлениями для старых ссылок и общими действиями.
-->
<template>
  <div class="container edo-document-details">
    <base-loader v-if="isLoading" />

    <div v-else-if="document" class="edo-document-details__content">
      <base-nav
        :title="pageTitle"
        :show-back="true"
        back-fallback="EdoDocuments"
        :back-fallback-pattern="/\/edo\/documents/"
      >
        <base-primary-button
          class="nav-btn"
          icon="share"
          @click="createSharing"
        >
          Поделиться
        </base-primary-button>
        <base-primary-button
          v-if="copyAvailable"
          class="nav-btn"
          :disabled="isCopying"
          @click="isCopyDialogVisible = true"
        >
          <AppIcon class="nav-btn__icon" name="copy" :size="16" />
          {{ isCopying ? "Готовим дубликат..." : "Дублировать" }}
        </base-primary-button>
        <base-primary-button
          v-if="document.xml_url"
          class="nav-btn"
          @click="downloadXml"
        >
          <AppIcon class="nav-btn__icon" name="download" :size="16" />
          Скачать XML
        </base-primary-button>
        <base-primary-button
          v-if="!document.external_id"
          class="nav-btn"
          @click="goToDraftEdit"
        >
          <AppIcon class="nav-btn__icon" name="send" :size="16" />
          Отправить в Saby
        </base-primary-button>
        <base-primary-button
          v-if="document.external_id && sabyUrl"
          class="nav-btn"
          @click="openSabyUrl"
        >
          <AppIcon class="nav-btn__icon" name="externalLink" :size="16" />
          Открыть в Saby
        </base-primary-button>
        <base-primary-button
          v-if="document.external_id"
          class="nav-btn"
          :disabled="isRefreshing"
          @click="handleRefresh"
        >
          <AppIcon class="nav-btn__icon" name="sync" :size="16" />
          {{ isRefreshing ? "Обновляем..." : "Обновить из Saby" }}
        </base-primary-button>
        <BaseArchiveRestoreButton
          class="nav-btn"
          :archived="Boolean(document.archived_at)"
          :disabled="isSubmitting"
          :loading="isArchiveActionLoading"
          archive-title="Архивировать ЭДО-документ?"
          archive-message="Карточка будет убрана из обычного списка. Документ в Saby не изменится."
          restore-title="Восстановить ЭДО-документ?"
          restore-message="Карточка снова появится в обычном списке."
          @click="handleArchiveAction"
        />
      </base-nav>

      <BaseDialog
        :visible="isCopyDialogVisible"
        title="Дублировать документ?"
        message="Создадим новый черновик на основе этого документа: перенесём основные данные, а номер, дату, статусы и подписи начнём заново. Исходный документ останется без изменений."
        :actions="copyDialogActions"
        @action="handleCopyDialogAction"
      />

      <div class="tab-content">
        <div v-if="activeTab === 'info'" class="edo-document-info-layout">
          <main class="edo-document-info-layout__main">
            <ConsignmentNoteInfoTab
              v-if="etrnInfoVisible"
              :document="document"
              :saby-url="sabyUrl"
              @create-title1="goToTitle1Build"
              @edit-title2="goToConsignmentNoteTitle2Edit"
              @edit-title3="goToConsignmentNoteTitle3Edit"
              @edit-title4="goToConsignmentNoteTitle4Edit"
              @edit-redirect="goToConsignmentNoteRedirectEdit"
              @edit-replacement="goToConsignmentNoteReplacementEdit"
            />

            <ForwarderOrderInfoTab
              v-else-if="forwarderOrderInfoVisible"
              :document="document"
              :saby-url="sabyUrl"
              @edit-title1="goToForwarderOrderEdit"
              @edit-title2="goToForwarderOrderTitle2Edit"
              @edit-receipt="goToForwarderReceiptEdit"
            />

            <ForwarderReceiptInfoTab
              v-else-if="forwarderReceiptInfoVisible"
              :document="document"
              :saby-url="sabyUrl"
              @edit-title1="goToForwarderOrderEdit"
              @edit-title2="goToForwarderOrderTitle2Edit"
              @edit-receipt="goToForwarderReceiptEdit"
            />

            <TransportOrderPreviewTab
              v-else-if="transportOrderFormVisible"
              :document="document"
              :saby-url="sabyUrl"
              @edit="goToTransportOrderEdit"
              @edit-title2="goToTransportOrderTitle2Edit"
              @create-consignment-note="goToConsignmentNoteFromTransportOrder"
            />

            <EdoDocumentExtraTab
              v-else
              :document="document"
              :attribute-labels="attributeLabels"
              :summary-only="true"
            />
          </main>

          <aside class="edo-document-info-layout__aside">
            <EdoDocumentInternalStatus
              :document="document"
              :is-submitting="isSubmitting"
              @save="handleInternalDetailsSave"
              @update-source-links="handleSourceLinksUpdate"
            />
            <EdoDocumentPdfPreviewCard
              :available="pdfTabVisible"
              :pending="pdfPendingVisible"
              @open="openPdfPreview"
            />
          </aside>
        </div>

        <EdoDocumentPdfTab
          v-else-if="activeTab === 'pdf'"
          :document="document"
          :visible="pdfTabVisible"
          :pdf-pending-visible="pdfPendingVisible"
          :pdf-polling-hint="pdfPollingHint"
          :is-pdf-loading="isPdfLoading"
          :pdf-load-error="pdfLoadError"
          :pdf-preview-url="pdfPreviewUrl"
          @open-pdf="openPdfInTab"
          @download-pdf="downloadPdf"
          @pdf-loaded="isPdfLoading = false"
          @pdf-error="onPdfError"
          @refresh-pdf="handleRefreshPdf"
        />

        <EdoDocumentExtraTab
          v-else-if="activeTab === 'extra'"
          :document="document"
          :attribute-labels="attributeLabels"
          :xml-validation-result-override="xmlValidationResultOverride"
          :is-refreshing="isRefreshing"
          :is-validating-xml="isValidatingXml"
          :is-updating-source-links="isSubmitting"
          @update-source-links="handleSourceLinksUpdate"
          @validate-xml="handleValidateXml"
        />
      </div>

      <EdoDocumentPdfPreviewModal
        :visible="isPdfPreviewVisible"
        :document="document"
        :pdf-visible="pdfTabVisible"
        :pdf-pending-visible="pdfPendingVisible"
        :pdf-polling-hint="pdfPollingHint"
        :is-pdf-loading="isPdfLoading"
        :pdf-load-error="pdfLoadError"
        :pdf-preview-url="pdfPreviewUrl"
        @close="isPdfPreviewVisible = false"
        @open-pdf="openPdfInTab"
        @download-pdf="downloadPdf"
        @pdf-loaded="isPdfLoading = false"
        @pdf-error="onPdfError"
        @refresh-pdf="handleRefreshPdf"
      />
    </div>

    <div v-else class="no-data">Документ ЭДО не найден</div>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";

import { useEdoDocumentStore } from "@/stores/edoDocumentStore";
import { useNotificationStore } from "@/stores/notificationStore";

import AppIcon from "@/components/Icon/AppIcon.vue";
import EdoDocumentExtraTab from "@/components/Edo/DocumentExtraTab.vue";
import EdoDocumentPdfTab from "@/components/Edo/DocumentPdfTab.vue";
import EdoDocumentPdfPreviewCard from "@/components/Edo/DocumentPdfPreviewCard.vue";
import EdoDocumentPdfPreviewModal from "@/components/Edo/DocumentPdfPreviewModal.vue";
import ForwarderOrderInfoTab from "@/components/Edo/ForwarderOrder/InfoTab.vue";
import ForwarderReceiptInfoTab from "@/components/Edo/ForwarderReceipt/InfoTab.vue";
import TransportOrderPreviewTab from "@/components/Edo/TransportOrder/PreviewTab.vue";
import ConsignmentNoteInfoTab from "@/components/Edo/ConsignmentNote/InfoTab.vue";
import EdoDocumentInternalStatus from "@/components/Edo/DocumentInternalStatus.vue";

import {
  edoDocumentResolvedType,
  edoDocumentTypeLabel,
} from "@/utils/edoDocumentLabels";
import {
  isProviderPdfExpected,
  shouldRefreshPdfOnOpen,
} from "@/utils/edoDocumentPdfUtils";
import { resolveUrl } from "@/utils/fileUtils";
import { createSingleFlight } from "@/utils/singleFlight";

const PDF_POLL_INTERVALS = [
  15_000,
  30_000,
  60_000,
  180_000,
  600_000,
  1_800_000,
  1_800_000,
];

export default {
  name: "EdoDocumentDetailsPage",

  components: {
    AppIcon,
    EdoDocumentExtraTab,
    EdoDocumentPdfTab,
    EdoDocumentPdfPreviewCard,
    EdoDocumentPdfPreviewModal,
    ForwarderOrderInfoTab,
    ForwarderReceiptInfoTab,
    TransportOrderPreviewTab,
    ConsignmentNoteInfoTab,
    EdoDocumentInternalStatus,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      activeTab: "info",
      isPdfPreviewVisible: false,
      isRefreshing: false,
      isCopying: false,
      isCopyDialogVisible: false,
      isArchiveActionLoading: false,
      isValidatingXml: false,
      xmlValidationResultOverride: null,
      attributeLabels: {},
      isPdfLoading: true,
      pdfLoadError: null,
      pdfPollTimer: null,
      pdfPollAttempt: 0,
      isPdfPolling: false,
      pdfPollingFinished: false,
      pdfPreviewUrl: "",
      preparePdfPreviewOnce: createSingleFlight(),
    };
  },

  computed: {
    ...mapState(useEdoDocumentStore, [
      "selectedDocument",
      "isLoading",
      "isSubmitting",
    ]),

    document() {
      return this.selectedDocument;
    },

    pageTitle() {
      const number = this.document?.document_number;
      const type = this.documentTypeLabel(this.document);
      return number ? `${type} №${number}` : type;
    },

    sabyUrl() {
      return this.getSabyUrl(this.document);
    },

    copyAvailable() {
      return [
        "TransportOrder",
        "ConsignmentNote",
        "ForwarderOrder",
        "ForwarderReceipt",
      ].includes(this.document?.document_type) &&
        Boolean(this.document?.parsed_data);
    },

    copyDialogActions() {
      return [
        {
          key: "close",
          label: "Отмена",
          type: "secondary",
          disabled: this.isCopying,
        },
        {
          key: "confirm",
          label: this.isCopying ? "Готовим дубликат..." : "Дублировать",
          type: "primary",
          disabled: this.isCopying,
        },
      ];
    },

    defaultTab() {
      return "info";
    },

    resolvedDocumentType() {
      return edoDocumentResolvedType(this.document);
    },

    etrnInfoVisible() {
      return this.resolvedDocumentType === "ConsignmentNote";
    },

    transportOrderFormVisible() {
      return this.document?.form_available === true;
    },

    forwarderOrderInfoVisible() {
      return this.resolvedDocumentType === "ForwarderOrder";
    },

    forwarderReceiptInfoVisible() {
      return this.resolvedDocumentType === "ForwarderReceipt";
    },

    pdfTabVisible() {
      return Boolean(
        this.document?.pdf_url ||
          this.document?.xml_url ||
          this.pdfExpected
      );
    },

    pdfPendingVisible() {
      return (
        Boolean(this.document?.id) &&
        this.pdfExpected &&
        !this.document?.pdf_url &&
        !this.pdfPollingFinished
      );
    },

    pdfExpected() {
      return isProviderPdfExpected(this.document);
    },

    pdfPollingHint() {
      if (this.isPdfPolling) return "Проверяем готовность PDF...";
      if (this.pdfPollAttempt > 0) {
        return "Страница обновится автоматически, когда PDF появится";
      }
      return "Обычно файл появляется через несколько минут после создания документа";
    },
  },

  watch: {
    id() {
      this.resetDocumentState();
      this.fetchDocument(this.id).then(() => {
        this.syncActiveTabWithRoute();
        this.startPdfPollingIfNeeded();
      });
    },

    "$route.query": {
      handler() {
        this.syncActiveTabWithRoute();
      },
      immediate: true,
    },

    "document.pdf_url"(newUrl, oldUrl) {
      if (newUrl && newUrl !== oldUrl) {
        this.preparePdfPreview();
        this.stopPdfPolling();
      }
    },

    document() {
      this.syncActiveTabWithRoute();
    },

    activeTab(newTab, oldTab) {
      if (newTab !== oldTab) {
        this.updateUrlTab(newTab);
      }
      if (newTab === "pdf") {
        this.refreshPdfTabDocument();
      }
    },
  },

  async created() {
    await Promise.all([
      this.fetchDocument(this.id),
      this.loadAttributeLabels(),
    ]);
    this.syncActiveTabWithRoute();
    this.startPdfPollingIfNeeded();
  },

  beforeUnmount() {
    this.stopPdfPolling();
    this.revokePdfPreviewUrl();
  },

  methods: {
    createSharing() {
      this.$router.push({
        name: "SharingCreate",
        query: {
          shareableType: "Edo::Document",
          shareableId: this.document.id,
          tripId: this.document.trip_id || undefined,
        },
      });
    },

    ...mapActions(useEdoDocumentStore, [
      "fetchDocument",
      "fetchDocumentSilently",
      "refreshDocument",
      "validateDocumentXml",
      "fetchDocumentAttributeLabels",
      "fetchDuplicatePayload",
      "downloadDocumentFile",
      "downloadDocumentPdf",
      "updateDocumentSourceLinks",
      "updateInternalDetails",
      "archiveDocument",
      "restoreDocument",
    ]),

    async handleSourceLinksUpdate(sourceLinks) {
      try {
        await this.updateDocumentSourceLinks(this.document.id, sourceLinks);
      } catch {
        // API errors are shown by the global interceptor.
      }
    },

    async handleInternalDetailsSave(payload) {
      try {
        await this.updateInternalDetails(this.document.id, payload);
      } catch {
        // Ошибку показывает глобальный обработчик API.
      }
    },

    async handleArchiveAction() {
      if (!this.document) return;

      this.isArchiveActionLoading = true;
      try {
        if (this.document.archived_at) {
          await this.restoreDocument(this.document.id);
        } else {
          await this.archiveDocument(this.document.id);
        }
      } catch {
        // Ошибку показывает глобальный обработчик API.
      } finally {
        this.isArchiveActionLoading = false;
      }
    },

    async copyDocument() {
      this.isCopying = true;
      try {
        const payload = await this.fetchDuplicatePayload(this.document.id);
        const routeNameByBuildType = {
          transport_order: "EdoTransportOrderBuild",
          consignment_note_title1: "EdoConsignmentNoteBuild",
          forwarder_order: "EdoForwarderOrderBuild",
          forwarder_receipt: "EdoForwarderReceiptBuild",
        };
        const routeName = routeNameByBuildType[payload.build_type];
        if (!routeName) throw new Error("Неподдерживаемый тип документа");

        const copyKey = `edo-copy:${Date.now()}:${this.document.id}`;
        sessionStorage.setItem(copyKey, JSON.stringify(payload));
        await this.$router.push({
          name: routeName,
          query: {
            copy_key: copyKey,
            integration_id: payload.integration_id || undefined,
            source_order_id: payload.order_id || undefined,
            source_trip_id: payload.trip_id || undefined,
          },
        });
        this.isCopyDialogVisible = false;
      } finally {
        this.isCopying = false;
      }
    },

    handleCopyDialogAction(action) {
      if (action === "confirm") {
        this.copyDocument();
      } else if (!this.isCopying) {
        this.isCopyDialogVisible = false;
      }
    },

    resetDocumentState() {
      this.xmlValidationResultOverride = null;
      this.isPdfLoading = true;
      this.pdfLoadError = null;
      this.isPdfPreviewVisible = false;
      this.stopPdfPolling();
      this.pdfPollingFinished = false;
    },

    async handleRefreshPdf() {
      await this.refreshPdfTabDocument({ forceRefresh: true });
    },

    openPdfPreview() {
      if (!this.pdfTabVisible) return;

      this.isPdfPreviewVisible = true;
      this.refreshPdfTabDocument();
    },

    async refreshPdfTabDocument({ forceRefresh = false } = {}) {
      if (!this.document?.id) return;
      if (!forceRefresh && !shouldRefreshPdfOnOpen(this.document)) {
        if (this.document.pdf_url) {
          await this.preparePdfPreview();
        } else {
          this.isPdfLoading = false;
        }
        this.pdfLoadError = null;
        return;
      }

      this.isPdfLoading = true;
      this.pdfLoadError = null;

      try {
        const updated = this.pdfExpected && (!this.document.pdf_url || forceRefresh)
          ? await this.refreshDocument(this.document.id)
          : await this.fetchDocumentSilently(this.document.id);

        if (updated?.pdf_url) {
          await this.preparePdfPreview({ force: forceRefresh });
          this.stopPdfPolling();
        }
      } catch (error) {
        console.warn("Не удалось обновить документ при открытии PDF:", error);
        this.isPdfLoading = false;
        this.pdfLoadError = true;
      }
    },

    preparePdfPreview({ force = false } = {}) {
      if (this.pdfPreviewUrl && !force) return Promise.resolve();

      return this.preparePdfPreviewOnce(() => this.loadPdfPreview());
    },

    async loadPdfPreview() {
      if (!this.document?.pdf_url) {
        this.revokePdfPreviewUrl();
        this.isPdfLoading = false;
        return;
      }

      this.isPdfLoading = true;
      this.pdfLoadError = null;
      this.revokePdfPreviewUrl();

      try {
        const response = await this.downloadDocumentPdf(this.document.id);
        const blob = this.pdfBlob(response.data);

        // PDF-превью не даём читать signed S3 URL напрямую: ссылка может
        // открываться в новой вкладке, но pdf.js внутри SPA упирается в CORS.
        // Поэтому сначала забираем файл как blob и уже его передаём viewer.
        this.pdfPreviewUrl = URL.createObjectURL(blob);
      } catch (error) {
        console.warn("Не удалось подготовить PDF-превью:", error);
        this.isPdfLoading = false;
        this.pdfLoadError = true;
      }
    },

    pdfBlob(data) {
      if (data instanceof Blob && data.type === "application/pdf") return data;

      return new Blob([data], { type: "application/pdf" });
    },

    revokePdfPreviewUrl() {
      if (!this.pdfPreviewUrl) return;

      URL.revokeObjectURL(this.pdfPreviewUrl);
      this.pdfPreviewUrl = "";
    },

    syncActiveTabWithRoute() {
      if (!this.document) return;

      // Скрытые представления сохраняем для старых прямых ссылок,
      // но в обычном интерфейсе навигации к ним больше нет.
      const viewKeys = ["info", "extra"];
      if (this.pdfTabVisible) viewKeys.push("pdf");

      const requestedTab = this.$route.query.tab;
      const normalizedRequestedTab = ["form", "forwarding"].includes(requestedTab)
        ? "info"
        : requestedTab;
      const nextTab = viewKeys.includes(normalizedRequestedTab)
        ? normalizedRequestedTab
        : this.defaultTab;

      if (this.activeTab !== nextTab) {
        this.activeTab = nextTab;
      }

      if (requestedTab !== nextTab) {
        this.updateUrlTab(nextTab);
      }
    },

    updateUrlTab(tabName) {
      if (this.$route.query.tab === tabName) return;

      this.$router
        .replace({ query: { ...this.$route.query, tab: tabName } })
        .catch(() => {});
    },

    getSabyUrl(document) {
      return (
        document?.saby_url ||
        document?.saby_link ||
        document?.saby_document_url ||
        document?.saby_document_link ||
        document?.edo_url ||
        document?.edo_link ||
        document?.edo_document_url ||
        document?.edo_document_link ||
        document?.document_url ||
        document?.document_link ||
        document?.provider_url ||
        document?.provider_link ||
        document?.external_url ||
        document?.external_link ||
        document?.external_document_url ||
        document?.external_documents_url ||
        document?.edo_documents_url ||
        document?.edo_documents_link ||
        document?.documents_url ||
        document?.documents_link ||
        document?.links?.saby ||
        document?.links?.edo ||
        document?.links?.document ||
        document?.urls?.saby ||
        document?.urls?.edo ||
        document?.urls?.document ||
        null
      );
    },

    async loadAttributeLabels() {
      try {
        const attrs = await this.fetchDocumentAttributeLabels();
        this.attributeLabels = attrs.reduce((acc, attr) => {
          if (attr.path && attr.label) {
            acc[attr.path] = attr.label;
          }
          return acc;
        }, {});
      } catch (error) {
        console.warn("Не удалось загрузить переводы полей ЭДО:", error);
        this.attributeLabels = {};
      }
    },

    documentTypeLabel(documentOrType) {
      return edoDocumentTypeLabel(documentOrType);
    },

    openSabyUrl() {
      window.open(this.sabyUrl, "_blank");
    },

    goToDraftEdit() {
      const type = this.resolvedDocumentType;
      const id = this.document.id;
      const routes = {
        TransportOrder: { name: "EdoTransportOrderEdit", params: { id } },
        ConsignmentNote: { name: "EdoConsignmentNoteBuild", query: { draft_id: id } },
        ForwarderOrder: { name: "EdoForwarderOrderEdit", params: { id } },
        ForwarderReceipt: { name: "EdoForwarderReceiptEdit", params: { id } },
      };
      const route = routes[type];
      if (route) this.$router.push(route);
    },

    async downloadXml() {
      const url = resolveUrl(this.document.xml_url);
      try {
        const response = await this.downloadDocumentFile(url);
        const blobUrl = URL.createObjectURL(
          new Blob([response.data], { type: "application/xml" })
        );
        const filename = this.document.document_number
          ? `${this.document.document_number}.xml`
          : "document.xml";
        const a = Object.assign(document.createElement("a"), {
          href: blobUrl,
          download: filename,
        });
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(url, "_blank");
      }
    },

    async downloadPdf() {
      const url = resolveUrl(this.document.pdf_url);
      try {
        const response = await this.downloadDocumentPdf(this.document.id);
        const blobUrl = URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        );
        const filename = this.document.document_number
          ? `${this.document.document_number}.pdf`
          : "document.pdf";
        const a = Object.assign(document.createElement("a"), {
          href: blobUrl,
          download: filename,
        });
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(url, "_blank");
      }
    },

    async openPdfInTab() {
      const url = resolveUrl(this.document.pdf_url);
      try {
        const response = await this.downloadDocumentPdf(this.document.id);
        const blobUrl = URL.createObjectURL(
          new Blob([response.data], { type: "application/pdf" })
        );
        const tab = window.open(blobUrl, "_blank");
        if (tab) {
          setTimeout(() => URL.revokeObjectURL(blobUrl), 30000);
        }
      } catch {
        window.open(url, "_blank");
      }
    },

    onPdfError(error) {
      console.warn("Ошибка загрузки PDF:", error);
      this.isPdfLoading = false;
      this.pdfLoadError = true;
    },

    startPdfPollingIfNeeded() {
      if (!this.pdfPendingVisible || this.pdfPollTimer) return;

      this.pdfPollAttempt = 0;
      this.scheduleNextPdfPoll();
    },

    scheduleNextPdfPoll() {
      if (!this.pdfPendingVisible) {
        this.stopPdfPolling();
        return;
      }

      const delay = PDF_POLL_INTERVALS[this.pdfPollAttempt];
      if (!delay) {
        this.pdfPollingFinished = true;
        this.stopPdfPolling();
        return;
      }

      this.pdfPollTimer = setTimeout(this.pollPdfAvailability, delay);
    },

    async pollPdfAvailability() {
      this.pdfPollTimer = null;
      if (!this.pdfPendingVisible) return;

      this.isPdfPolling = true;
      this.pdfPollAttempt += 1;

      try {
        const updated = await this.fetchDocumentSilently(this.document.id);
        if (updated?.pdf_url) {
          await this.preparePdfPreview();
          this.stopPdfPolling();
          return;
        }
      } catch (error) {
        console.warn("Не удалось проверить готовность PDF ЭДО:", error);
      } finally {
        this.isPdfPolling = false;
      }

      this.scheduleNextPdfPoll();
    },

    stopPdfPolling() {
      if (this.pdfPollTimer) {
        clearTimeout(this.pdfPollTimer);
        this.pdfPollTimer = null;
      }
      this.isPdfPolling = false;
    },

    async handleRefresh() {
      this.isRefreshing = true;
      try {
        await this.refreshDocument(this.document.id);
        useNotificationStore().showSuccess("Документ обновлён из Saby");
      } catch {
        // API errors are shown by the global interceptor.
      } finally {
        this.isRefreshing = false;
      }
    },

    async handleValidateXml() {
      const notificationStore = useNotificationStore();
      this.isValidatingXml = true;
      this.xmlValidationResultOverride = null;

      try {
        await this.refreshDocument(this.document.id);
        const result = await this.validateDocumentXml(this.document.id);
        this.xmlValidationResultOverride = result;

        if (result.valid) {
          notificationStore.showSuccess("XML обновлён из Saby и прошёл проверку");
        } else {
          notificationStore.showError(
            `XML обновлён из Saby, найдены ошибки: ${result.errors?.length || 0}`
          );
        }
      } catch {
        // API errors are shown by the global interceptor.
      } finally {
        this.isValidatingXml = false;
      }
    },

    async goToTitle1Build() {
      if (!this.document.external_id) {
        this.$router.push({ name: "EdoConsignmentNoteBuild", query: { draft_id: this.document.id } });
      } else {
        await this.refreshDocument(this.document.id);
        this.$router.push({ name: "EdoConsignmentNoteBuild", query: { edit_id: this.document.id } });
      }
    },

    goToConsignmentNoteFromTransportOrder() {
      const query = {
        source_transport_order_id: this.document.id,
        source_order_id: this.document.order_id,
        source_trip_id: this.document.trip_id,
        integration_id: this.document.integration_id,
      };

      Object.keys(query).forEach((key) => {
        if (!query[key]) delete query[key];
      });

      this.$router.push({
        name: "EdoConsignmentNoteBuild",
        query,
      });
    },

    goToTransportOrderEdit() {
      this.$router.push({
        name: "EdoTransportOrderEdit",
        params: { id: this.document.id },
      });
    },

    goToTransportOrderTitle2Edit() {
      this.$router.push({
        name: "EdoTransportOrderTitle2Edit",
        params: { id: this.document.id },
      });
    },

    goToForwarderOrderEdit() {
      this.$router.push({
        name: "EdoForwarderOrderEdit",
        params: { id: this.document.id },
      });
    },

    goToForwarderOrderTitle2Edit() {
      this.$router.push({
        name: "EdoForwarderOrderTitle2Edit",
        params: { id: this.document.id },
      });
    },

    goToForwarderReceiptEdit() {
      this.$router.push({
        name: "EdoForwarderReceiptEdit",
        params: { id: this.document.id },
      });
    },

    goToConsignmentNoteTitle2Edit() {
      this.$router.push({
        name: "EdoConsignmentNoteTitle2Edit",
        params: { id: this.document.id },
      });
    },

    goToConsignmentNoteTitle3Edit() {
      this.$router.push({
        name: "EdoConsignmentNoteTitle3Edit",
        params: { id: this.document.id },
      });
    },

    goToConsignmentNoteTitle4Edit() {
      this.$router.push({
        name: "EdoConsignmentNoteTitle4Edit",
        params: { id: this.document.id },
      });
    },

    goToConsignmentNoteRedirectEdit() {
      this.$router.push({
        name: "EdoConsignmentNoteRedirectEdit",
        params: { id: this.document.id },
      });
    },

    goToConsignmentNoteReplacementEdit() {
      this.$router.push({
        name: "EdoConsignmentNoteReplacementEdit",
        params: { id: this.document.id },
      });
    },
  },
};
</script>

<style scoped>
.edo-document-details {
  flex-grow: 1;
}

.edo-document-details__content {
  display: flex;
  flex-direction: column;
  gap: var(--base-out-indent);
}

.tab-content {
  min-width: 0;
}

.edo-document-info-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 16px;
  align-items: start;
}

.edo-document-info-layout__main,
.edo-document-info-layout__aside {
  min-width: 0;
}

.edo-document-info-layout__aside {
  display: grid;
  gap: 16px;
}

.nav-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.nav-btn__icon {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
}

.no-data {
  color: #6b7280;
}

@media (max-width: 900px) {
  .edo-document-info-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
